import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AutocompleteField, BaseOption } from '@reckon-web/autocomplete';
import { Checkbox } from '@reckon-web/checkbox';
import { Stack } from '@reckon-web/stack';
import { Notice } from '@reckon-web/notice';
import { Text } from '@reckon-web/text';
import { Divider } from '@reckon-web/divider';
import { Heading } from '@reckon-web/heading';
import { Tag } from '@reckon-web/tag';
import { Inline } from '@reckon-web/inline';
import { Flex } from '@reckon-web/flex';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';
import { Tooltip } from '@reckon-web/tooltip';
import {
  Form,
  FormValue,
  InitialValueInput,
  ValidatedFormValue,
  field,
  validate,
} from '@reckon-web/forms';

import { useGetJobStatusList } from '../../../data/useGetJobStatusList';
import { TextField } from '../../common/text-field';
import { nameStatus } from '../../../utils/name-status-utils';
import { useClientAutoComplete } from '../../../hooks/use-client-auto-complete';
import { useGetEntityTypes } from '../../../data/useGetEntityTypes';
import { CurrencyField } from '../../common/currency-field';
import { DatePickerField } from '../../common/date-picker-field';
import { SelectField } from '../../common/select-field';
import { RecurrenceFrequencyEnum } from '../types/recurrence-frequency-enum';
import { TaskStatusEnum } from '../../work/types/task-status-enum';
import { useDebtorAutoComplete } from '../../../hooks/use-debtor-auto-complete';
import { useQueryTemplates } from '../../../hooks/use-query-templates';
import { useGetMembersList } from '../../../data/useGetMembersList';
import { useGetClientAsync } from '../../../data/useGetClient';
import { useGetEntitiesAsync } from '../../../data/useGetEntities';
import {
  GetTemplateQueryVariables,
  useGetTemplate,
} from '../../../data/useGetTemplate';
import { TemplateTaskJobUserTypeEnum } from '../../manage/templates/types/template-task-job-user-type-enum';
import {
  GetEntitiesByIdsQueryVariables,
  useGetEntitiesByIds,
} from '../../../data/useGetEntitiesByIds';
import { Task } from '../../../hooks/use-query-tasks';
import { JobStatusEnum } from '../types/job-status-enum';
import type { GetMyDetailsQueryUser } from '../../../data/useGetMyDetails';

type SelectedClient = {
  name: string;
  cloudId: string;
};

const RecurrenceFrequency = RecurrenceFrequencyEnum.valuesEnum;

const recurrenceFrequencyOptions: BaseOption[] = [
  {
    label: 'Week',
    value: RecurrenceFrequency.SevenDays,
  },
  {
    label: 'Fortnight',
    value: RecurrenceFrequency.FourteenDays,
  },
  {
    label: '1 month',
    value: RecurrenceFrequency.OneMonth,
  },
  {
    label: '2 months',
    value: RecurrenceFrequency.TwoMonths,
  },
  {
    label: '3 months',
    value: RecurrenceFrequency.ThreeMonths,
  },
  {
    label: '6 months',
    value: RecurrenceFrequency.SixMonths,
  },
  {
    label: '12 months',
    value: RecurrenceFrequency.TwelveMonths,
  },
];

function usersToOptions(
  users: GetMyDetailsQueryUser[] | undefined,
  matching: (u: GetMyDetailsQueryUser) => boolean
): BaseOption[] {
  if (!users?.length) {
    return [];
  }
  // make sure we also include the currently selected user, regardless of if they're able to be assigned a task
  // reason: need to be able to edit other details of the task
  // and retain the current assignee
  const assignableUsers = users.filter(matching);

  assignableUsers.sort((a, b) => {
    const aActive = a?.licensed && a?.active;
    const bActive = b?.licensed && b?.active;
    if (aActive && !bActive) {
      return -1;
    }
    if (!aActive && bActive) {
      return 1;
    }
    return (a?.fullName || '').localeCompare(b?.fullName || '', undefined, {
      sensitivity: 'base',
    });
  });

  const userOptions = assignableUsers.map((u) => {
    return { value: u?.cloudId || '', label: nameStatus(u) };
  });

  userOptions.unshift({ label: 'None', value: '' });

  return userOptions;
}

export const jobFormSchema = field.object(
  {
    debtor: field.autocomplete()({
      validate: validate((value) => {
        validate.nonNullable(value);
        return value;
      }),
    }),
    clients: field.multiselect({
      validate: validate((value) => {
        if (!value || value.length === 0) {
          validate.error(`You must select at least one client`);
        }
        return value;
      }),
    }),
    budget: field.currency({
      validate: validate((value) => {
        // blank string is a valid value I guess?
        if (value === '') {
          return value;
        }
        let parsed: number;
        if (typeof value === 'string') {
          parsed = parseFloat(value);
        } else {
          parsed = value;
        }
        if (isNaN(parsed)) {
          validate.error('Must be a number');
        } else if (parsed < 0) {
          validate.positiveNumber(parsed);
        }
        return value;
      }),
    }),
    template: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    name: field.text({
      validate: validate((value) => {
        validate.required(value);
        return value;
      }),
    }),
    partner: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    manager: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    staff: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    assignee: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    startDate: field.date({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    dueDate: field.date({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    recurrenceEnabled: field.boolean({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    recurrenceNextInstance: field.date({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
    recurrenceFrequency: field.select()({
      validate: validate((value) => {
        // validate.required(value);
        return value;
      }),
    }),
  },
  {
    validate: {
      recurrenceNextInstance: validate.object((value, objectValue) => {
        if (objectValue.recurrenceEnabled && !value) {
          validate.error('Required');
        }
        return value;
      }),
      recurrenceFrequency: validate.object((value, objectValue) => {
        if (objectValue.recurrenceEnabled && !value) {
          validate.error('Required');
        }
        return value;
      }),
    },
  }
);

export type JobFormType = typeof jobFormSchema;
export type JobFormForm = Form<JobFormType>;
export type JobFormInitialValueInput = InitialValueInput<JobFormType>;
export type ValidatedJobFormValue = ValidatedFormValue<JobFormType>;
export type JobFormExternalErrors = Partial<
  Record<keyof FormValue<JobFormType>, string[]>
>;

export function getArrayThumbprint(array?: string[] | null) {
  return JSON.stringify(
    [...(array || [])].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    )
  );
}

export type JobFormCloseArgs =
  | {
      didCancel: true;
    }
  | {
      didCancel: false;
      jobId: string;
    };

export type JobFormMode = 'create' | 'edit' | 'recur';
export type JobFormProps = {
  mode: JobFormMode;
  form: JobFormForm;
  /** for showing prompts when something differs from the original value */
  formInitialValues?: JobFormInitialValueInput;
  setTasks?: (tasks: Task[]) => void;
  externalErrors?: JobFormExternalErrors;
  testId?: string;
};

export const JobForm: React.FC<JobFormProps> = ({
  mode,
  form,
  formInitialValues,
  setTasks,
  externalErrors,
  testId = `job-form`,
}) => {
  // tuck away the incoming errors - we'll hide them as they edit the fields, so we need a copy of them
  const [externalErrorsCopy, setExternalErrorsCopy] =
    useState<JobFormExternalErrors>();
  useEffect(() => {
    setExternalErrorsCopy(externalErrors);
  }, [externalErrors]);

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isRecur = mode === 'recur';

  const { data: statuses } = useGetJobStatusList();
  const notStartedStatus = useMemo(() => {
    return statuses?.find(
      (x) => x.status === JobStatusEnum.valuesEnum.NotStarted
    );
  }, [statuses]);

  const searchDebtors = useDebtorAutoComplete();
  const searchClients = useClientAutoComplete();

  const doSearchDebtors = useCallback(
    async (inputValue: string): Promise<BaseOption[]> => {
      const debtors = await searchDebtors({
        includeNonClients: false,
        searchTerm: inputValue,
        take: 20,
      });
      if (debtors.data) {
        return debtors.data.map((d) => {
          return {
            value: d.cloudId,
            label: d.name || '',
          };
        });
      }
      return [];
    },
    [searchDebtors]
  );

  // We want 20 to show (arbitrary number)
  // But we also don't want any existing selections to show up
  // So, we'll request 20 + selected.length
  // Then we'll remove existing selections from the result
  // Then we'll slice the to 20 of that off and call it "the results"
  // This way, we'll only run out of results to show them when they legit select everything matching the search
  // rather than when they select the first 20 of the results
  // This would be better if the search took in an "exclude" list too. But it does not
  const doSearchClients = async (inputValue: string): Promise<BaseOption[]> => {
    const clients = await searchClients({
      includeNonEntities: false,
      searchTerm: inputValue,
      take: 20 + form.value.clients.length,
    });
    if (clients.data) {
      const unselectedClients = clients.data.filter(
        (c) => !form.value.clients.includes(c.cloudId)
      );
      const result = unselectedClients.map((c) => {
        return {
          value: c.cloudId,
          label: c.name || '',
        };
      });
      // slice end is exclusive, and slice will return a partial array if there's less than 20 in there
      return result.slice(0, 20);
    }
    return [];
  };

  // this is a bit heavyweight for what we want, but it'll do until we write a slimmer version. All we need is a name and an ID
  const {
    loading: templatesLoading,
    error: templatesError,
    data: templatesData,
  } = useQueryTemplates();

  const templateOptions = useMemo(() => {
    if (templatesLoading || templatesError || !templatesData) {
      return [];
    }

    const options = templatesData
      .filter((t) => !t.wfDeletedOnUTC)
      .map((t) => ({ value: t.cloudId || '', label: t.name || '' }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
    options.unshift({ label: 'None', value: '' });
    return options;
  }, [templatesLoading, templatesError, templatesData]);

  // get the selected template
  const getTemplateArgs: GetTemplateQueryVariables | undefined = useMemo(() => {
    if (!form.value.template) {
      return undefined;
    }
    return {
      cloudId: form.value.template,
    };
  }, [form.value.template]);

  const { data: templateData } = useGetTemplate({
    variables: getTemplateArgs,
    skip: !getTemplateArgs,
  });

  const {
    loading: usersLoading,
    data: usersData,
    error: usersError,
  } = useGetMembersList();

  const partnerOptions: BaseOption[] = useMemo(() => {
    if (usersLoading || usersError || !usersData) {
      return [];
    }
    return usersToOptions(
      usersData,
      (u) =>
        Boolean(u.active && u.licensed && u.partner) ||
        // important to be able to retain an invalid user
        u.cloudId === formInitialValues?.partner
    );
  }, [usersLoading, usersData, usersError, formInitialValues]);
  const managerOptions: BaseOption[] = useMemo(() => {
    if (usersLoading || usersError || !usersData) {
      return [];
    }
    return usersToOptions(
      usersData,
      (u) =>
        Boolean(u.active && u.licensed && u.manager) ||
        // important to be able to retain an invalid user
        u.cloudId === formInitialValues?.manager
    );
  }, [usersLoading, usersData, usersError, formInitialValues]);
  const staffOptions: BaseOption[] = useMemo(() => {
    if (usersLoading || usersError || !usersData) {
      return [];
    }
    return usersToOptions(
      usersData,
      (u) =>
        Boolean(u.active && u.licensed) ||
        // important to be able to retain an invalid user
        u.cloudId === formInitialValues?.staff
    );
  }, [usersLoading, usersData, usersError, formInitialValues]);
  const assigneeOptions: BaseOption[] = useMemo(() => {
    if (usersLoading || usersError || !usersData) {
      return [];
    }
    return usersToOptions(
      usersData,
      (u) =>
        Boolean(u.active && u.licensed) ||
        // important to be able to retain an invalid user
        u.cloudId === formInitialValues?.assignee
    );
  }, [usersLoading, usersData, usersError, formInitialValues]);

  // this is for calling from the dropdown
  // don't want to use the normal pattern of memo for params, hook with skip and useEffect on data
  // because we don't want it to fire when initialising the form
  // only when actually changing a value from the dropdown in the onChange
  const [isLoadingDebtor, setIsLoadingDebtor] = useState<boolean>();
  const getClient = useGetClientAsync();
  const getEntities = useGetEntitiesAsync();
  const onChangeDebtor = useCallback(
    async (cloudId: string | undefined) => {
      if (!cloudId) {
        return;
      }
      setIsLoadingDebtor(true);
      const { data: debtor } = await getClient({ cloudId });
      if (debtor) {
        const newPartner = debtor.partnerId || undefined;
        if (newPartner !== form.value.partner) {
          if (
            newPartner &&
            partnerOptions.some((x) => x.value === newPartner)
          ) {
            form.fields.partner.setState((p) => ({
              ...p,
              value: newPartner,
            }));
          } else {
            form.fields.partner.setState((p) => ({
              ...p,
              value: undefined,
            }));
          }
        }
        const newManager = debtor.managerId || undefined;
        if (newManager !== form.value.manager) {
          if (
            newManager &&
            managerOptions.some((x) => x.value === newManager)
          ) {
            form.fields.manager.setState((p) => ({
              ...p,
              value: newManager,
            }));
          } else {
            form.fields.manager.setState((p) => ({
              ...p,
              value: undefined,
            }));
          }
        }
        const newStaff = debtor.staffId || undefined;
        if (newStaff !== form.value.staff) {
          if (newStaff && staffOptions.some((x) => x.value === newStaff)) {
            form.fields.staff.setState((p) => ({
              ...p,
              value: newStaff,
            }));
          } else {
            form.fields.staff.setState((p) => ({
              ...p,
              value: undefined,
            }));
          }
        }
        // we have no default assignee, so no need to do anything here
      }

      const { data: entities } = await getEntities({ clientId: cloudId });
      form.fields.clients.setState((p) => ({
        ...p,
        value: (entities ?? [])
          .filter((x) => !x.isDeleted && !x.endDateUtc)
          .map((c) => c.cloudId),
      }));
      setSelectedClients(
        entities
          ?.map((x) => ({
            name: x.name || '',
            cloudId: x.cloudId,
          }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          )
      );
      setIsLoadingDebtor(false);
    },
    [form, getClient, getEntities, partnerOptions, managerOptions, staffOptions]
  );

  const initialGetEntitiesByIdsArgs =
    formInitialValues?.clients && formInitialValues.clients.length > 0
      ? {
          cloudIds: formInitialValues.clients,
        }
      : undefined;

  const {
    loading: initialGetEntitiesByIdsLoading,
    // error: initialGetEntitiesByIdsError,
    data: initialGetEntitiesByIdsData,
  } = useGetEntitiesByIds({
    variables: initialGetEntitiesByIdsArgs,
    skip: !initialGetEntitiesByIdsArgs,
  });

  const [selectedClients, setSelectedClients] = useState<SelectedClient[]>();
  useEffect(() => {
    if (initialGetEntitiesByIdsLoading) {
      return;
    }
    setSelectedClients(
      initialGetEntitiesByIdsData
        ?.map((x) => ({
          name: x.name || '',
          cloudId: x.cloudId,
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
    );
  }, [initialGetEntitiesByIdsLoading, initialGetEntitiesByIdsData]);

  // need the entity types to do the template inflation
  const { data: entityTypesData } = useGetEntityTypes();

  // need the selected entities (the actual selected entities, not just the initial ones, or the name and ID for display)
  // to do template inflation
  // We only want to do this based off the initial values
  // when anything is changed, we'll upate the selected entities stored in state
  // we only want to do this in create mode too
  const getEntitiesByIdsArgs: GetEntitiesByIdsQueryVariables | undefined =
    useMemo(() => {
      if (form.value.clients.length === 0 || mode !== 'create') {
        return undefined;
      }
      return {
        cloudIds: form.value.clients,
      };
    }, [form.value.clients, mode]);

  const {
    // loading: getEntitiesByIdsLoading,
    // error: getEntitiesByIdsError,
    data: getEntitiesByIdsData,
  } = useGetEntitiesByIds({
    variables: getEntitiesByIdsArgs,
    skip: !getEntitiesByIdsArgs,
  });

  const defaultTasks: Task[] = useMemo(() => [], []);

  // combine the template, the entities and the users together to make some tasks
  const tasks = useMemo(() => {
    if (
      mode !== 'create' ||
      !entityTypesData ||
      !templateData ||
      !getEntitiesByIdsData ||
      !usersData ||
      !templateData.entities ||
      !templateData.templateTasks?.length ||
      !notStartedStatus
    ) {
      return defaultTasks;
    }

    // first, find the general entity type
    const generalEntityType = entityTypesData.find(
      (x) => x.isGeneral && templateData.entities?.includes(x.cloudId)
    );
    // so that we can grab the general tasks. These ones are not created for an entity
    const generalTasks = generalEntityType
      ? templateData.templateTasks
          ?.filter(
            (tt) =>
              tt.entityTypeId === generalEntityType.cloudId &&
              !tt.wfDeletedOnUTC
          )
          .map((tt) => ({ task: tt, entities: [{}] })) // NOTE: Do not remove the empty array. It's required to map general tasks properly
      : [];

    // Grab the template tasks where the entity type for the template task is supposed to be included in the template
    const includedTemplateTasks = templateData.templateTasks.filter(
      (tt) =>
        templateData.entities?.includes(tt.entityTypeId!) && !tt.wfDeletedOnUTC
    );
    // then get the entities that go with each template task (where the entity type is the same)
    const tasksForEntities = includedTemplateTasks.map((tt) => {
      const entitiesForTask = getEntitiesByIdsData.filter(
        (e) => e.entityTypeId === tt.entityTypeId
      );
      return { task: tt, entities: entitiesForTask };
    });

    const templateTasksToInflate = [...generalTasks, ...tasksForEntities];

    const groupsOfTasks = templateTasksToInflate.map(({ task, entities }) => {
      // bit of pfaffery to work out who to actually assign the task to
      const assigneeId =
        task.jobUserType === TemplateTaskJobUserTypeEnum.valuesEnum.Parter
          ? form.value.partner
          : task.jobUserType === TemplateTaskJobUserTypeEnum.valuesEnum.Manager
            ? form.value.manager
            : task.jobUserType === TemplateTaskJobUserTypeEnum.valuesEnum.Staff
              ? form.value.staff
              : task.jobUserType ===
                  TemplateTaskJobUserTypeEnum.valuesEnum.Assignee
                ? form.value.assignee
                : task.ownerUserId;

      const assignee = usersData.find((x) => x.cloudId === assigneeId);

      return entities.map((e) => {
        const result = {
          cloudId: uuidv4(),
          jobId: '', // we don't know this until save time. Guess we could make it up earlier in the piece
          name: task.name,
          taskTypeId: task.taskTypeId,
          assignedToUserId: assignee ? assigneeId : undefined,
          assignee,
          entity: e,
          estimateMinutes: task.estimateMinutes,
          status: TaskStatusEnum.valuesEnum.NotStarted,
          dueDate: null,
          completedDate: null,
          wfDeletedOnUTC: null,
          job: {
            cloudId: '', // we don't know this until save time. Guess we could make it up earlier in the piece
            status: notStartedStatus,
          },
        };
        return result;
      }) as Task[];
    });

    const results = groupsOfTasks.flatMap((x) => x);
    return results;
  }, [
    templateData,
    getEntitiesByIdsData,
    entityTypesData,
    usersData,
    form.value.partner,
    form.value.manager,
    form.value.staff,
    form.value.assignee,
    mode,
    defaultTasks,
    notStartedStatus,
  ]);

  useEffect(() => {
    if (mode === 'create' && setTasks) {
      setTasks(tasks);
    }
  }, [setTasks, tasks, mode]);

  // couple of booleans for if things have changed from their original values
  // I feel that this could also be done looking at various touched/dirty/??? properties on the form
  // but, I know this will work
  const hasRecurrenceChanged = useMemo(() => {
    // only need to show the info if editing, not if creating or recurring
    if (!isEdit || !formInitialValues) {
      return false;
    }

    return (
      (formInitialValues.recurrenceEnabled || false) !==
        form.value.recurrenceEnabled ||
      (formInitialValues.recurrenceFrequency || '') !==
        (form.value.recurrenceFrequency || '') ||
      (formInitialValues.recurrenceNextInstance || '') !==
        (form.value.recurrenceNextInstance || '')
    );
  }, [
    formInitialValues,
    form.value.recurrenceEnabled,
    form.value.recurrenceFrequency,
    form.value.recurrenceNextInstance,
    isEdit,
  ]);

  const initialEntitiesThumbprint = useMemo(
    () => getArrayThumbprint(formInitialValues?.clients),
    [formInitialValues]
  );

  const haveClientsChanged = useMemo(() => {
    // only need to show the warning about changing clients when editing too
    // which is weird, in the old system we don't warn them about removing clients will remove the tasks
    // on the recur screen... because we don't actually remove the tasks
    // I dislike this, but that's how it's implemented
    if (!isEdit || !initialEntitiesThumbprint) {
      return false;
    }

    const currentThumbprint = getArrayThumbprint(form.value.clients);
    return currentThumbprint !== initialEntitiesThumbprint;
  }, [initialEntitiesThumbprint, form.value.clients, isEdit]);

  return (
    // bit of a hack with the padding. When editing, we are in a drawer. When creating/recurring, we are in a dialog
    // we only want the padding when in the dialog, it's the easiest way to style it without a wrapping element around this in the dialog
    // could pass this as a prop if this ever gets TOO horrible to look at
    <Stack
      gap="xlarge"
      paddingX={isEdit ? 'none' : 'xlarge'}
      data={{ testid: testId }}
    >
      {/* only want to change the debtor in create mode (which includes recur mode) */}
      {/* TODO what do we want to show for edit mode? Just the name? */}
      {(isCreate || isRecur) && (
        <AutocompleteField
          {...form.fields.debtor.props}
          label="Debtor*"
          placeholder="Search to add debtor"
          loadOptions={doSearchDebtors}
          invalidMessage={
            form.fields.debtor.props.invalidMessage ||
            externalErrorsCopy?.debtor?.join('. ')
          }
          onChange={(selectedOption) => {
            form.fields.debtor.props.onChange(selectedOption);
            // drop the selected clients and users
            // they'll get populated again (maybe)
            // but, clear them out and disable them first
            form.fields.clients.props.onChange([]);
            form.fields.partner.props.onChange(undefined);
            form.fields.manager.props.onChange(undefined);
            form.fields.staff.props.onChange(undefined);
            // no need to clear assignee when changing debtor, we have no default debtor
            onChangeDebtor(selectedOption?.value);
            setExternalErrorsCopy((v) => {
              return {
                ...v,
                debtor: [],
              };
            });
          }}
          /* TODO WORKFLOW-400 once balance supports test id's, replace this ID */
          id={makeId(testId, 'debtor')}
        />
      )}
      <Flex direction="column" gap="medium">
        {(initialGetEntitiesByIdsLoading || isLoadingDebtor) && (
          <LoadingDots
            label="Loading clients"
            data-testid={makeId(testId, 'loading-clients')}
          />
        )}
        {!initialGetEntitiesByIdsLoading && !isLoadingDebtor && (
          <AutocompleteField
            {...form.fields.clients.props}
            label="Clients*"
            placeholder="Search to add clients"
            loadOptions={doSearchClients}
            // we'll never hold a value here, have to overwrite this otherwise the multiselect props don't spread properly onto an autocomplete. Understandably too
            value={null}
            invalidMessage={
              form.fields.clients.props.invalidMessage ||
              externalErrorsCopy?.clients?.join('. ')
            }
            onChange={(selectedOption) => {
              // this won't ever be null/undefined, but typescript doesn't know that
              if (!selectedOption) {
                return;
              }

              // add it into the form
              form.fields.clients.setState((pv) => {
                if (pv.value.some((pe) => pe === selectedOption?.value)) {
                  return pv;
                }
                return {
                  ...pv,
                  touched: true,
                  value: pv.value.concat(selectedOption.value),
                };
              });

              setExternalErrorsCopy((v) => {
                return {
                  ...v,
                  clients: [],
                };
              });

              // and add it into the selected values
              setSelectedClients((value) => {
                const results = [
                  ...(value || []),
                  { name: selectedOption.label, cloudId: selectedOption.value },
                ];
                results.sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                  })
                );
                return results;
              });
            }}
            /* TODO WORKFLOW-400 once balance supports test id's, replace this ID */
            id={makeId(testId, 'clients')}
          />
        )}
        {/* the list of clients */}
        {!initialGetEntitiesByIdsLoading &&
          !isLoadingDebtor &&
          selectedClients && (
            <Inline gap="small">
              {selectedClients.map((e) => (
                <Tooltip
                  key={e.cloudId}
                  content={<Text overflowStrategy="breakword">{e.name}</Text>}
                >
                  <Tag
                    label={e.name}
                    onRemove={() => {
                      // remove from the form
                      form.fields.clients.setState((pv) => ({
                        ...pv,
                        touched: true,
                        value: pv.value.filter((pe) => pe !== e.cloudId),
                      }));
                      // AND remove from the selected clients
                      setSelectedClients((value) =>
                        value?.filter((x) => x.cloudId !== e.cloudId)
                      );
                    }}
                    data={{ testid: makeId(testId, 'tag-clients', e.cloudId) }}
                  />
                </Tooltip>
              ))}
            </Inline>
          )}
      </Flex>
      {/* TODO: Should not be visible if user has only ADDED new clients? */}
      {haveClientsChanged && (
        <Notice
          size="small"
          tone="cautious"
          title="Heads up"
          data-testid={makeId(testId, 'clients-changed-notice')}
        >
          <Stack gap="small">
            <Text size="xsmall">
              If you remove clients from this job, tasks associated with that
              client will be deleted when you save changes.
            </Text>
            <Text size="xsmall">
              Also, if this job was created from a template, tasks will not be
              created for any clients you have added.
            </Text>
          </Stack>
        </Notice>
      )}
      <CurrencyField
        {...form.fields.budget.props}
        label="Budget"
        invalidMessage={
          form.fields.budget.props.invalidMessage ||
          externalErrorsCopy?.budget?.join('. ')
        }
        onChange={(value) => {
          form.fields.budget.props.onChange(value);

          setExternalErrorsCopy((v) => {
            return {
              ...v,
              budget: [],
            };
          });
        }}
        /* TODO WORKFLOW-400 once balance supports test id's, replace this ID */
        id={makeId(testId, 'budget')}
      />
      {/* can only choose a template in create mode. Not when editing, not when recurring */}
      {isCreate && (
        <SelectField
          {...form.fields.template.props}
          label="Task template"
          description="Quickly populate your new job with a list of tasks generated by the selected template."
          placeholder="Select template"
          options={templateOptions}
          disabled={templatesLoading || templatesError}
          invalidMessage={
            form.fields.template.props.invalidMessage ||
            externalErrorsCopy?.template?.join('. ')
          }
          onChange={(value) => {
            form.fields.template.props.onChange(value || undefined);

            // if they've picked a template, use that as the job name
            if (value) {
              const selectedTemplate = templateOptions.find(
                (x) => x.value === value
              );
              if (selectedTemplate) {
                form.fields.name.setState((prev) => ({
                  ...prev,
                  value: selectedTemplate.label,
                }));
              }
            }
            setExternalErrorsCopy((v) => {
              return {
                ...v,
                template: [],
              };
            });
          }}
          data-testid={makeId(testId, 'task-template')}
        />
      )}
      <TextField
        {...form.fields.name.props}
        label="Job name*"
        placeholder="Job name"
        invalidMessage={
          form.fields.name.props.invalidMessage ||
          externalErrorsCopy?.name?.join('. ')
        }
        onChange={(e) => {
          form.fields.name.props.onChange(e);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              name: [],
            };
          });
        }}
        data-testid={makeId(testId, 'job-name')}
      />
      <SelectField
        {...form.fields.assignee.props}
        label="Assignee"
        placeholder={usersLoading ? 'Loading...' : 'Select assignee...'}
        options={assigneeOptions}
        disabled={usersLoading}
        invalidMessage={
          form.fields.assignee.props.invalidMessage ||
          externalErrorsCopy?.assignee?.join('. ')
        }
        onChange={(value) => {
          form.fields.assignee.props.onChange(value || undefined);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              assignee: [],
            };
          });
        }}
        data-testid={makeId(testId, 'assignee')}
      />
      <Divider />
      <Heading level="4">Management team</Heading>
      <SelectField
        {...form.fields.partner.props}
        label="Partner"
        placeholder={
          usersLoading || isLoadingDebtor ? 'Loading...' : 'Select partner...'
        }
        options={partnerOptions}
        disabled={usersLoading || isLoadingDebtor}
        invalidMessage={
          form.fields.partner.props.invalidMessage ||
          externalErrorsCopy?.partner?.join('. ')
        }
        onChange={(value) => {
          form.fields.partner.props.onChange(value || undefined);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              partner: [],
            };
          });
        }}
        data-testid={makeId(testId, 'partner')}
      />
      <SelectField
        {...form.fields.manager.props}
        label="Manager"
        placeholder={
          usersLoading || isLoadingDebtor ? 'Loading...' : 'Select manager...'
        }
        options={managerOptions}
        disabled={usersLoading || isLoadingDebtor}
        invalidMessage={
          form.fields.manager.props.invalidMessage ||
          externalErrorsCopy?.manager?.join('. ')
        }
        onChange={(value) => {
          form.fields.manager.props.onChange(value || undefined);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              manager: [],
            };
          });
        }}
        data-testid={makeId(testId, 'manager')}
      />
      <SelectField
        {...form.fields.staff.props}
        label="Staff"
        placeholder={
          usersLoading || isLoadingDebtor ? 'Loading...' : 'Select staff...'
        }
        options={staffOptions}
        disabled={usersLoading || isLoadingDebtor}
        invalidMessage={
          form.fields.staff.props.invalidMessage ||
          externalErrorsCopy?.staff?.join('. ')
        }
        onChange={(value) => {
          form.fields.staff.props.onChange(value || undefined);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              staff: [],
            };
          });
        }}
        data-testid={makeId(testId, 'staff')}
      />
      <Divider />
      <Heading level="4">Timeline</Heading>
      <DatePickerField
        {...form.fields.startDate.props}
        label="Start date"
        invalidMessage={
          form.fields.startDate.props.invalidMessage ||
          externalErrorsCopy?.startDate?.join('. ')
        }
        onChange={(value) => {
          form.fields.startDate.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              startDate: [],
            };
          });
        }}
        data-testid={makeId(testId, 'start-date')}
      />
      <DatePickerField
        {...form.fields.dueDate.props}
        label="Due date"
        invalidMessage={
          form.fields.dueDate.props.invalidMessage ||
          externalErrorsCopy?.dueDate?.join('. ')
        }
        onChange={(value) => {
          form.fields.dueDate.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              dueDate: [],
            };
          });
        }}
        data-testid={makeId(testId, 'due-date')}
      />
      <Divider />
      <Heading level="4">Recurrence</Heading>
      <Checkbox
        // {...form.fields.recurrenceEnabled.props}
        checked={form.fields.recurrenceEnabled.props.value}
        // TODO where's the validation message for a checkbox? Not that we need one. But, still
        onChange={(e) => {
          form.fields.recurrenceEnabled.props.onChange(e.target.checked);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              recurrenceEnabled: [],
            };
          });
        }}
        data-testid={makeId(testId, 'checkbox-recurrence')}
      >
        This job recurs
      </Checkbox>
      <DatePickerField
        {...form.fields.recurrenceNextInstance.props}
        label="Next instance"
        disabled={!form.value.recurrenceEnabled}
        invalidMessage={
          form.fields.recurrenceNextInstance.props.invalidMessage ||
          externalErrorsCopy?.recurrenceNextInstance?.join('. ')
        }
        onChange={(value) => {
          form.fields.recurrenceNextInstance.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              recurrenceNextInstance: [],
            };
          });
        }}
        data-testid={makeId(testId, 'next-instance')}
      />
      <SelectField
        {...form.fields.recurrenceFrequency.props}
        label="Then every..."
        placeholder="Select frequency..."
        options={recurrenceFrequencyOptions}
        disabled={!form.value.recurrenceEnabled}
        invalidMessage={
          form.fields.recurrenceFrequency.props.invalidMessage ||
          externalErrorsCopy?.recurrenceFrequency?.join('. ')
        }
        onChange={(value) => {
          form.fields.recurrenceFrequency.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              recurrenceFrequency: [],
            };
          });
        }}
        data-testid={makeId(testId, 'frequency')}
      />
      {hasRecurrenceChanged && form.value.recurrenceEnabled && (
        <Notice
          tone="informative"
          data-testid={makeId(testId, 'recurrence-changed-notice')}
        >
          <Text size="small">
            NOTE: when you recur this job, the due date on the job and its tasks
            will be incremented by the recurrence period above.
          </Text>
        </Notice>
      )}
    </Stack>
  );
};
